import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Handshake,
  Layers,
  ShieldCheck,
  Zap,
} from "lucide-react";
import MultiStepCalculator from "@/components/MultiStepCalculator";
import ParallaxGlassField from "@/components/ParallaxGlassField";
import ScrollStory from "@/components/ScrollStory";
import SectionEyebrow from "@/components/SectionEyebrow";
import StatStrip from "@/components/StatStrip";
import useLandingMotion from "@/hooks/useLandingMotion";

const HERO_STATS = [
  { value: "3-24h", label: "Decision Speed", detail: "Fast review window" },
  { value: "24-48h", label: "Funded In", detail: "After approval" },
  { value: "37", label: "Products", detail: "Matched to your profile" },
  { value: "Dedicated", label: "Guidance", detail: "Advisor support" },
];

const PRODUCTS = [
  {
    to: "/long-term-loans",
    title: "Long-Term Business Loans",
    range: "$50K – $5M",
    term: "1 – 10 years",
    body: "Predictable monthly payments for sustained growth, expansion, or refinancing.",
  },
  {
    to: "/mca",
    title: "Cash Injection (MCA)",
    range: "$5K – $500K",
    term: "3 – 18 months",
    body: "Fast capital with revenue-aligned remittances. Funded as fast as same-day.",
  },
  {
    to: "/heloc-calculator",
    title: "HELOC Calculator",
    range: "$25K – $500K",
    term: "10 – 30 years",
    body: "Leverage your home equity for business capital with competitive rates.",
  },
  {
    to: "/funding-estimator",
    title: "Revenue-Based Estimator",
    range: "Instant Output",
    term: "Real-time",
    body: "Get matched to the right products based on revenue, credit, and time in business.",
  },
  {
    to: "/contact",
    title: "Equipment Financing",
    range: "$10K – $5M",
    term: "1 – 7 years",
    body: "Finance machinery, technology, or vehicles – collateralized by the asset.",
  },
  {
    to: "/contact",
    title: "Line of Credit",
    range: "$10K – $250K",
    term: "Revolving",
    body: "Draw what you need, when you need it. Only pay for what you use.",
  },
];

const FEATURES = [
  {
    icon: Zap,
    title: "Fast Decisions",
    body: "Typical review within 24-48 hours. No weeks of waiting for answers.",
  },
  {
    icon: ShieldCheck,
    title: "Transparent Terms",
    body: "Clear documentation, zero hidden fees. You understand exactly what you're getting.",
  },
  {
    icon: Layers,
    title: "Flexible Solutions",
    body: "Multiple products and structures adapted to your specific business stage.",
  },
  {
    icon: Handshake,
    title: "Long term finance partner",
    body: "We are looking to build long term partnerships with business owners. the company we work for is already 14 years in business, we are going to be here tommorow and 5 years from now.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "See your options",
    body: "Fill out our quick form and instantly see which funding products match your business profile.",
  },
  {
    n: "02",
    title: "Talk to a specialist",
    body: "Get instant answers and personalized guidance. We qualify your needs so we can serve you faster.",
  },
  {
    n: "03",
    title: "Get funded",
    body: "A funding specialist contacts you within 24 hours with a pre-qualified offer tailored to your business.",
  },
];

export default function HomePage() {
  const pageRef = useRef(null);
  useLandingMotion(pageRef);

  return (
    <div ref={pageRef} className="landing-motion-shell" data-testid="home-page">
      <div className="page-progress" aria-hidden="true">
        <span data-page-progress />
      </div>

      <section className="hero-section relative overflow-hidden" data-hero>
        <ParallaxGlassField />
        <img
          src="/images/hero_abstract.png"
          alt=""
          className="hero-media absolute inset-x-0 top-0 h-[760px] w-full object-cover opacity-[0.2] mix-blend-screen pointer-events-none [mask-image:linear-gradient(to_bottom,black_14%,transparent_88%)]"
          data-hero-media
        />
        <div className="hero-atmosphere" aria-hidden="true" />

        <div className="container-x relative pt-10 md:pt-16 pb-14 md:pb-20">
          <div className="mx-auto max-w-5xl text-center" data-hero-copy-block>
            <div
              className="hero-status inline-flex items-center gap-2 border border-cyan-300/15 bg-cyan-300/[0.035] px-3 py-1.5 text-xs text-zinc-300 shadow-[0_0_40px_rgba(34,211,238,0.08)]"
              data-hero-kicker
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
              Reviewing applications – Average decision in 3–24 hours
            </div>

            <h1 className="h1 hero-title mt-6 mx-auto max-w-5xl" data-hero-title>
              <span className="hero-title-line">
                <span data-hero-title-line>Capital that respects</span>
              </span>
              {" "}
              <span className="hero-title-line">
                <span className="gradient-text" data-hero-title-line>your timeline.</span>
              </span>
            </h1>

            <p className="body-lg mt-6 mx-auto max-w-2xl" data-hero-copy>
              See which funding products match your business – in seconds. No login required.
              Strategic, transparent, and structured for how you actually grow.
            </p>
          </div>

          <div className="hero-grid mt-9 md:mt-12 grid xl:grid-cols-[minmax(0,1fr)_minmax(620px,820px)_minmax(0,1fr)] gap-5 items-center">
            <div className="hidden xl:grid gap-4">
              {HERO_STATS.slice(0, 2).map((stat) => (
                <div key={stat.label} className="hero-proof-card" data-hero-stat>
                  <div className="font-mono text-2xl text-white">{stat.value}</div>
                  <div className="font-mono uppercase tracking-[0.16em] text-[0.68rem] text-emerald-300 mt-2">
                    {stat.label}
                  </div>
                  <div className="text-xs text-zinc-500 mt-2">{stat.detail}</div>
                </div>
              ))}
            </div>

            <div className="hero-calculator-shell hero-calculator-focus" data-hero-calculator>
              <MultiStepCalculator />
            </div>

            <div className="hidden xl:grid gap-4">
              {HERO_STATS.slice(2).map((stat) => (
                <div key={stat.label} className="hero-proof-card" data-hero-stat>
                  <div className="font-mono text-2xl text-white">{stat.value}</div>
                  <div className="font-mono uppercase tracking-[0.16em] text-[0.68rem] text-emerald-300 mt-2">
                    {stat.label}
                  </div>
                  <div className="text-xs text-zinc-500 mt-2">{stat.detail}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-7 xl:hidden" data-hero-stat>
            <StatStrip
              className="stat-glass"
              items={HERO_STATS.map(({ value, label }) => ({ value, label }))}
            />
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3" data-hero-actions>
            <Link to="/funding-estimator" data-testid="hero-cta-primary" className="btn-accent">
              Find what fits your business <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="hero-cta-secondary" className="btn-ghost">
              Start application <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section
        id="solutions"
        className="products-section container-x section-y scroll-mt-24"
        data-motion-section
      >
        <div className="motion-divider" data-motion-divider aria-hidden="true" />
        <SectionEyebrow
          label="Products"
          title="One platform. Multiple capital paths."
          description="From short-term cash injection to long-term structured loans – choose the structure that fits."
        />

        <div className="product-grid mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PRODUCTS.map((product, index) => (
            <Link
              key={product.title}
              to={product.to}
              data-testid={`product-card-${index}`}
              data-motion-card
              className="product-card card-surface p-7 group flex flex-col"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[0.72rem] text-emerald-400 uppercase tracking-[0.18em]">
                  {product.range}
                </span>
                <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-emerald-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
              </div>
              <h3 className="h3 mt-5">{product.title}</h3>
              <p className="body mt-3 text-[0.95rem] flex-1">{product.body}</p>
              <div className="mt-6 pt-5 border-t border-white/[0.05] text-xs text-zinc-500 font-mono uppercase tracking-[0.16em]">
                {product.term}
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="feature-section relative overflow-hidden" data-motion-section data-feature-section>
        <ParallaxGlassField className="opacity-35" density="light" />
        <div className="motion-divider" data-motion-divider aria-hidden="true" />
        <div className="container-x section-y relative">
          <div className="feature-layout grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-12 lg:gap-20 items-start">
            <div className="feature-heading lg:sticky lg:top-32" data-feature-heading>
              <SectionEyebrow
                label="Why TMF Team?"
                title="Speed paired with strategic thinking."
                description="Every funding solution is structured around your business reality – not a one-size product playbook."
              />
            </div>

            <div className="feature-rows">
              {FEATURES.map((feature, index) => (
                <article
                  key={feature.title}
                  className="feature-row"
                  data-feature-row
                  data-direction={index % 2 === 0 ? "forward" : "reverse"}
                  data-testid={`why-card-${index}`}
                >
                  <div className="feature-row-meta">
                    <span className="feature-row-index">0{index + 1}</span>
                    <span className="feature-icon">
                      <feature.icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="feature-row-copy">
                    <h3 className="h3">{feature.title}</h3>
                    <p className="body mt-3 text-[0.97rem]">{feature.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ScrollStory steps={STEPS} />

      <section className="container-x pb-24 md:pb-32" data-motion-section>
        <div className="motion-divider" data-motion-divider aria-hidden="true" />
        <div className="motion-cta relative overflow-hidden border border-white/[0.07] p-8 sm:p-10 md:p-16" data-motion-card>
          <img
            src="/images/data_flow.png"
            alt=""
            className="absolute inset-0 h-[112%] w-full object-cover opacity-45"
            data-parallax-media
          />
          <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(3,17,11,0.96),rgba(9,9,11,0.84)_58%,rgba(4,20,28,0.72))]" />
          <div className="motion-cta-beam" aria-hidden="true" />

          <div className="relative max-w-2xl" data-motion-heading>
            <div className="eyebrow">Get Started</div>
            <h2 className="h2 mt-4">Ready to explore your funding options?</h2>
            <p className="body-lg mt-5">
              Scroll up and use the funding calculator, or speak directly with our team for tailored guidance.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="cta-get-funded" className="btn-accent">
                Get funded <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/about" data-testid="cta-learn-more" className="btn-ghost">
                Learn about TMF Line
              </Link>
            </div>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-zinc-300">
              {[
                "No login required",
                "Soft credit-aware quotes",
                "24-48h decision turnaround",
                "Dedicated funding advisor",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
